import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Empty,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Steps,
    Tag,
    Typography,
} from 'antd';
import {
    ArrowRightOutlined,
    CheckCircleOutlined,
    ReloadOutlined,
    SearchOutlined,
    UserAddOutlined,
    UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { connect } from 'react-redux';
import { getPersonalRecordInformation, postMedicalRecordOrderAttention } from '../store/personalrecord/thunks';
import {
    getAppointmentsByOrganization,
    getOrganizationMedicById,
    getOrganizationMedics,
    getOrganizationManagementSpecialities,
} from '../store/user/thunks';
import { PatientForm } from './PatientManagement';
import styles from './DirectAttentionManagement.module.scss';

const { Text, Title } = Typography;
type AnyRecord = Record<string, any>;

interface DirectPatient {
    dni: string;
    firstName: string;
    secondName?: string;
    lastName: string;
    secondLastname?: string;
    birthdate?: string;
    gender?: string;
    civilStatus?: string;
    typeRelationship?: string;
    levelStudy?: string;
    profession?: string;
    fingerPrintCode?: string;
    phone?: AnyRecord;
    address?: AnyRecord;
    email?: string;
    demographicData?: AnyRecord;
}

interface SpecialtyOption {
    id: string;
    name: string;
    triageSigns?: boolean;
    status?: string;
    isAssociated?: boolean;
}

interface MedicOption {
    id: string;
    name: string;
    centerId?: string;
    centerName?: string;
    specialities: SpecialtyOption[];
}

interface ExistingAppointment {
    id?: string;
    dni?: string;
    userName?: string;
    userId?: string;
    specialityId?: string;
    specialityName?: string;
    centerId?: string;
    centerName?: string;
    date?: string;
    hour?: string;
    status?: string;
    type?: string;
}

interface DirectAttentionManagementProps {
    getPersonalRecordInformation?: any;
    postMedicalRecordOrderAttention?: any;
    getAppointmentsByOrganization?: any;
    getOrganizationMedicById?: any;
    getOrganizationMedics?: any;
    getOrganizationManagementSpecialities?: any;
    organizationId?: string;
    organizationName?: string;
}

const valueOf = (source: AnyRecord | null | undefined, ...keys: string[]) => {
    if (!source) return undefined;
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') return source[key];
    }
    return undefined;
};

const parseJsonValue = (source: any) => {
    if (typeof source !== 'string') return source;
    try {
        return JSON.parse(source);
    } catch {
        return source;
    }
};

const unwrapPatient = (source: AnyRecord | null | undefined) => valueOf(source, 'personalRecord', 'PersonalRecord') || source;

const normalizePatient = (sourceInput: AnyRecord | null | undefined): DirectPatient | null => {
    const source = unwrapPatient(sourceInput);
    if (!source) return null;
    const rawPhone = valueOf(source, 'phone', 'Phone');
    const phone = (Array.isArray(rawPhone) ? rawPhone[0] : rawPhone) || {};
    const rawAddresses = valueOf(source, 'addresses', 'Addresses', 'address', 'Address');
    const address = Array.isArray(rawAddresses) ? rawAddresses[0] : rawAddresses;
    const rawEmails = valueOf(source, 'emailPersonalRecords', 'EmailPersonalRecords', 'email', 'Email');
    const emailRecords = Array.isArray(rawEmails) ? rawEmails : rawEmails ? [rawEmails] : [];
    const email = typeof rawEmails === 'string' ? rawEmails : valueOf(emailRecords[0], 'description', 'Description');
    const patient: DirectPatient = {
        dni: String(valueOf(source, 'dni', 'Dni') || ''),
        firstName: String(valueOf(source, 'firstName', 'FirstName') || ''),
        secondName: valueOf(source, 'secondName', 'SecondName'),
        lastName: String(valueOf(source, 'lastName', 'LastName') || ''),
        secondLastname: valueOf(source, 'secondLastname', 'SecondLastname'),
        birthdate: valueOf(source, 'birthdate', 'Birthdate'),
        gender: valueOf(source, 'gender', 'Gender'),
        civilStatus: valueOf(source, 'civilStatus', 'CivilStatus') || valueOf(valueOf(source, 'demographicData', 'DemographicData'), 'civilStatus', 'CivilStatus'),
        typeRelationship: valueOf(source, 'typeRelationship', 'TypeRelationship'),
        levelStudy: valueOf(source, 'levelStudy', 'LevelStudy'),
        profession: valueOf(source, 'profession', 'Profession'),
        fingerPrintCode: valueOf(source, 'fingerPrintCode', 'FingerPrintCode'),
        phone,
        address,
        email,
        demographicData: valueOf(source, 'demographicData', 'DemographicData'),
    };
    return patient.dni ? patient : null;
};

const responseArray = (source: any, ...keys: string[]) => {
    const parsedSource = parseJsonValue(source);
    if (Array.isArray(parsedSource)) return parsedSource;
    const values = parseJsonValue(valueOf(parsedSource, ...keys, 'items', 'Items', 'data', 'Data'));
    return Array.isArray(values) ? values : [];
};

const normalizeSpecialties = (source: any): SpecialtyOption[] => {
    return responseArray(source, 'specialities', 'Specialities', 'speciality', 'Speciality')
    .map((item: AnyRecord) => ({
        id: String(valueOf(item, 'id', 'Id', 'specialityId', 'SpecialityId') || ''),
        name: String(valueOf(item, 'name', 'Name', 'specialityName', 'SpecialityName') || ''),
        triageSigns: Boolean(valueOf(item, 'triageSigns', 'TriageSigns')),
        status: String(valueOf(item, 'status', 'Status') || 'ACTIVO').toUpperCase(),
        isAssociated: valueOf(item, 'isAssociated', 'IsAssociated') === undefined
            ? true
            : Boolean(valueOf(item, 'isAssociated', 'IsAssociated')),
    }))
    .filter((item: SpecialtyOption) => item.id && item.name);
};

const normalizeMedic = (source: AnyRecord): MedicOption => {
    const detail = valueOf(source, 'speciality', 'Speciality', 'specialities', 'Specialities');
    return {
        id: String(valueOf(source, 'id', 'Id') || ''),
        name: String(valueOf(source, 'fullName', 'FullName') || 'Médico sin nombre'),
        centerId: valueOf(source, 'centerId', 'CenterId', 'center.id', 'Center.Id'),
        centerName: valueOf(source, 'centerName', 'CenterName', 'center.name', 'Center.Name'),
        specialities: normalizeSpecialties(detail),
    };
};

const normalizeAppointments = (source: any): ExistingAppointment[] => responseArray(source, 'appointments', 'Appointments').map((item: AnyRecord) => ({
    id: valueOf(item, 'id', 'Id', 'appointmentId', 'AppointmentId'),
    dni: valueOf(item, 'dni', 'Dni'),
    userName: valueOf(item, 'userName', 'UserName'),
    userId: valueOf(item, 'userId', 'UserId'),
    specialityId: valueOf(item, 'specialityId', 'SpecialityId', 'specialtyId', 'SpecialtyId'),
    specialityName: valueOf(item, 'specialityName', 'SpecialityName'),
    centerId: valueOf(item, 'centerId', 'CenterId'),
    centerName: valueOf(item, 'centerName', 'CenterName'),
    date: valueOf(item, 'date', 'Date'),
    hour: valueOf(item, 'hour', 'Hour'),
    status: valueOf(item, 'status', 'Status'),
    type: valueOf(item, 'type', 'Type'),
}));

const dateKey = (value?: string) => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    const parts = value.split('/');
    if (parts.length === 3) return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
    return dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
};

const isLinkableAppointment = (appointment: ExistingAppointment) => {
    const status = String(appointment.status || '').toUpperCase();
    return !['CANCELADO', 'ANULADO', 'PROCESADO', 'ATENDIDO', 'COMPLETADO'].some(value => status.includes(value));
};

const DirectAttentionManagement: React.FC<DirectAttentionManagementProps> = ({
    getPersonalRecordInformation: findPatient,
    postMedicalRecordOrderAttention: createOrderAttention,
    getAppointmentsByOrganization: loadAppointments,
    getOrganizationMedicById: loadMedicDetail,
    getOrganizationMedics: loadMedics,
    getOrganizationManagementSpecialities: loadOrganizationSpecialities,
    organizationId,
    organizationName,
}) => {
    const [dni, setDni] = useState('');
    const [patient, setPatient] = useState<DirectPatient | null>(null);
    const [patientSearchLoading, setPatientSearchLoading] = useState(false);
    const [patientModalOpen, setPatientModalOpen] = useState(false);
    const [patientReady, setPatientReady] = useState(false);
    const [specialties, setSpecialties] = useState<SpecialtyOption[]>([]);
    const [medics, setMedics] = useState<MedicOption[]>([]);
    const [appointments, setAppointments] = useState<ExistingAppointment[]>([]);
    const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<string>();
    const [selectedMedicId, setSelectedMedicId] = useState<string>();
    const [selectedAppointment, setSelectedAppointment] = useState<ExistingAppointment>();
    const [attentionType, setAttentionType] = useState('CONSULTA');
    const [paymentType, setPaymentType] = useState('PAGO_POSTCONSULTA');
    const [observations, setObservations] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const selectedSpecialty = specialties.find(item => item.id === selectedSpecialtyId)
        || (selectedAppointment?.specialityId && selectedAppointment.specialityName
            ? {
                id: selectedAppointment.specialityId,
                name: selectedAppointment.specialityName,
                triageSigns: false,
            }
            : undefined);
    const selectedMedic = medics.find(item => item.id === selectedMedicId)
        || (selectedAppointment?.userId
            ? {
                id: selectedAppointment.userId,
                name: selectedAppointment.userName || 'Médico sin nombre',
                centerId: selectedAppointment.centerId,
                centerName: selectedAppointment.centerName,
                specialities: [],
            }
            : undefined);
    const filteredMedics = useMemo(() => selectedSpecialtyId
        ? medics.filter(item => item.specialities.some(speciality => speciality.id === selectedSpecialtyId))
        : [], [medics, selectedSpecialtyId]);
    const patientAppointments = useMemo(() => {
        if (!patient) return [];
        const today = dayjs().format('YYYY-MM-DD');
        return appointments.filter(appointment =>
            String(appointment.dni || '') === patient.dni
            && dateKey(appointment.date) === today
            && isLinkableAppointment(appointment)
            && !!appointment.id
        );
    }, [appointments, patient]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const [specialtyResponse, medicResponse, appointmentResponse] = await Promise.all([
                    loadOrganizationSpecialities?.(organizationId),
                    loadMedics?.(organizationId),
                    loadAppointments?.(organizationId),
                ]);
                const basicMedics = responseArray(medicResponse).map(normalizeMedic).filter(item => item.id);
                let detailedMedics = basicMedics;
                if (loadMedicDetail && basicMedics.length > 0) {
                    detailedMedics = await Promise.all(basicMedics.map(async (medic) => {
                        try {
                            const detailResponse = await loadMedicDetail(medic.id, organizationId);
                            const detail = detailResponse as AnyRecord;
                            return { ...medic, ...normalizeMedic({ ...medic, ...detail }) };
                        } catch {
                            return medic;
                        }
                    }));
                }
                if (mounted) {
                    setSpecialties(normalizeSpecialties(specialtyResponse).filter(item => item.isAssociated && item.status === 'ACTIVO'));
                    setMedics(detailedMedics);
                    setAppointments(normalizeAppointments(appointmentResponse));
                }
            } catch (loadError) {
                if (mounted) setError(loadError instanceof Error ? loadError.message : 'No se pudo cargar la configuración de atención.');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [loadAppointments, loadMedicDetail, loadMedics, loadOrganizationSpecialities, organizationId]);

    const searchPatient = async () => {
        const value = dni.trim();
        if (!value) {
            setError('Ingrese la cédula del paciente.');
            return;
        }
        setError(null);
        setSuccess(null);
        setPatientReady(false);
        setPatientSearchLoading(true);
        try {
            const response = await findPatient?.(value, organizationId);
            const found = normalizePatient(response);
            setPatient(found);
            setPatientModalOpen(true);
        } catch {
            setPatient(null);
            setPatientModalOpen(true);
        } finally {
            setPatientSearchLoading(false);
        }
    };

    const finishPatient = async (savedDni: string) => {
        const response = await findPatient?.(savedDni, organizationId);
        const updated = normalizePatient(response);
        setPatient(updated || { dni: savedDni, firstName: '', lastName: '' });
        setDni(savedDni);
        setPatientReady(true);
        setPatientModalOpen(false);
        setError(null);
    };

    const resetConfiguration = () => {
        setSelectedSpecialtyId(undefined);
        setSelectedMedicId(undefined);
        setSelectedAppointment(undefined);
        setAttentionType('CONSULTA');
        setPaymentType('PAGO_POSTCONSULTA');
        setObservations('');
        setError(null);
    };

    const handleSpecialtyChange = (value: string) => {
        setSelectedSpecialtyId(value);
        setSelectedMedicId(undefined);
        setSelectedAppointment(undefined);
        setError(null);
    };

    const handleMedicChange = (value: string) => {
        setSelectedMedicId(value);
        setError(null);
    };

    const handleAppointmentChange = (appointmentId?: string) => {
        const appointment = patientAppointments.find(item => item.id === appointmentId);
        setSelectedAppointment(appointment);
        const appointmentSpecialty = specialties.find(item =>
            item.id === appointment?.specialityId
            || item.name === appointment?.specialityName
        );
        setSelectedSpecialtyId(appointment?.specialityId || appointmentSpecialty?.id);
        setSelectedMedicId(appointment?.userId);
        setError(null);
    };

    const submit = async () => {
        if (!patient || !selectedSpecialty || !selectedMedic) {
            setError('Complete paciente, especialidad y médico.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const timeStatus = selectedSpecialty.triageSigns ? 'ENFERMERIA' : 'CONSULTA';
            const response = await createOrderAttention?.({
                UserId: selectedMedic.id,
                Observations: observations.trim() || null,
                TimeStatus: timeStatus,
                PersonalRecordDni: patient.dni,
                Type: attentionType,
                TypePayment: paymentType,
                TypeVisit: 'INDIVIDUAL',
                SpecialityId: selectedSpecialty.id,
                SpecialityName: selectedSpecialty.name,
                OrganizationId: organizationId,
                OrganizationName: organizationName,
                CenterId: selectedMedic.centerId || selectedAppointment?.centerId || null,
                CenterName: selectedMedic.centerName || selectedAppointment?.centerName || null,
                AppointmentId: selectedAppointment?.id || null,
                AppointmentDate: selectedAppointment?.date && selectedAppointment?.hour
                    ? `${dateKey(selectedAppointment.date)}T${String(selectedAppointment.hour).slice(0, 8)}`
                    : null,
            });
            if (response === undefined) {
                throw new Error('No se pudo crear la orden de atención.');
            }

            const code = valueOf(response, 'orderAttentionCode', 'OrderAttentionCode');
            setSuccess(code
                ? `Orden de atención ${code} ${selectedAppointment ? 'vinculada a la cita' : 'aperturada'} correctamente.`
                : `Orden de atención ${selectedAppointment ? 'vinculada a la cita' : 'aperturada'} correctamente.`);
            resetConfiguration();
        } catch (submitError) {
            const message = submitError instanceof Error ? submitError.message : 'No se pudo completar la apertura de atención.';
            setError(message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className={styles.loading}><Spin /></div>;

    return <div className={styles.page}>
        <div className={styles.header}>
            <div>
                <Title level={3}>Apertura de atención</Title>
                <Text type="secondary">Apertura de órdenes para pacientes que llegan sin cita o que ya tienen una cita registrada.</Text>
            </div>
            <Tag color="blue">{organizationName || 'Organización seleccionada'}</Tag>
        </div>

        {error && <Alert type="error" showIcon message="No se puede continuar" description={error} closable onClose={() => setError(null)} />}
        {success && <Alert type="success" showIcon message="Proceso completado" description={success} action={<Button size="small" icon={<ReloadOutlined />} onClick={() => { setSuccess(null); setDni(''); setPatient(null); setPatientReady(false); resetConfiguration(); }}>Nueva atención</Button>} />}

        <Steps current={patientReady ? 1 : 0} items={[{ title: 'Paciente' }, { title: 'Atención' }]} />

        <Card className={styles.searchCard}>
            <div className={styles.sectionTitle}><UserOutlined /> <span>Identificar paciente</span></div>
            <Text type="secondary">Ingrese la cédula para buscar o registrar al paciente dentro de esta organización.</Text>
            <Space.Compact className={styles.searchBar}>
                <Input value={dni} onChange={event => setDni(event.target.value)} onPressEnter={searchPatient} placeholder="Cédula del paciente" maxLength={20} />
                <Button type="primary" icon={<SearchOutlined />} loading={patientSearchLoading} onClick={searchPatient}>Buscar</Button>
            </Space.Compact>
            {patientReady && patient && <div className={styles.patientFound}>
                <div className={styles.patientAvatar}>{`${patient.firstName?.[0] || ''}${patient.lastName?.[0] || ''}`.toUpperCase() || 'P'}</div>
                <div><Text strong>{[patient.firstName, patient.secondName, patient.lastName, patient.secondLastname].filter(Boolean).join(' ') || 'Paciente'}</Text><Text type="secondary">C.I. {patient.dni}</Text></div>
                <Button icon={<UserAddOutlined />} onClick={() => setPatientModalOpen(true)}>Revisar datos</Button>
            </div>}
        </Card>

        {!patientReady ? <Card className={styles.emptyStep}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Primero identifique al paciente para continuar" /></Card> : <>
            <Card className={styles.configurationCard}>
                <div className={styles.sectionTitle}><CheckCircleOutlined /> <span>Configurar atención</span></div>
                <Row gutter={[16, 8]}>
                    {patientAppointments.length > 0 && <Col xs={24}>
                        <Form.Item label="Cita del día (opcional)">
                            <Select
                                allowClear
                                value={selectedAppointment?.id}
                                onChange={handleAppointmentChange}
                                placeholder="Seleccione una cita para vincularla a la orden"
                                options={patientAppointments.map(item => ({
                                    value: item.id,
                                    label: `${String(item.hour || '').slice(0, 5)} · ${item.specialityName || 'Especialidad'} · ${item.userName || 'Médico'}`
                                }))}
                            />
                        </Form.Item>
                    </Col>}
                    <Col xs={24} md={12}>
                        <Form.Item label="Especialidad" required>
                            <Select value={selectedSpecialtyId} onChange={handleSpecialtyChange} disabled={Boolean(selectedAppointment)} placeholder="Seleccione la especialidad" options={specialties.map(item => ({ value: item.id, label: item.name }))} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label="Médico" required>
                            <Select value={selectedMedicId} onChange={handleMedicChange} disabled={Boolean(selectedAppointment) || !selectedSpecialtyId} placeholder={selectedSpecialtyId ? 'Seleccione el médico' : 'Seleccione primero la especialidad'} options={filteredMedics.map(item => ({ value: item.id, label: item.name }))} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Tipo de atención" required>
                            <Select value={attentionType} onChange={setAttentionType} options={[{ value: 'CONSULTA', label: 'Consulta' }, { value: 'PROCEDIMIENTO', label: 'Procedimiento' }, { value: 'DESPACHO', label: 'Despacho' }]} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item label="Tipo de pago" required>
                            <Select value={paymentType} onChange={setPaymentType} options={[{ value: 'PAGO_PREVIO', label: 'Pago previo' }, { value: 'PAGO_POSTCONSULTA', label: 'Pago postconsulta' }, { value: 'PEDIDO_EXTERNO', label: 'Pedido externo' }]} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={3}>
                        <Form.Item label="Tipo de visita"><Input value="INDIVIDUAL" disabled /></Form.Item>
                    </Col>
                    <Col xs={24} md={3}>
                        <Form.Item label="Estado inicial"><Input value={selectedSpecialty?.triageSigns ? 'ENFERMERIA' : 'CONSULTA'} disabled /></Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item label="Observaciones"><Input.TextArea value={observations} onChange={event => setObservations(event.target.value)} rows={3} placeholder="Detalle opcional de la llegada del paciente" /></Form.Item>
                    </Col>
                </Row>
                {selectedMedic && <Descriptions size="small" bordered column={{ xs: 1, md: 3 }} items={[
                    { key: 'specialty', label: 'Especialidad', children: selectedSpecialty?.name },
                    { key: 'medic', label: 'Médico', children: selectedMedic.name },
                    ...(selectedAppointment ? [{
                        key: 'appointment',
                        label: 'Cita vinculada',
                        children: `${String(selectedAppointment.hour || '').slice(0, 5)} · ${selectedAppointment.specialityName || selectedSpecialty?.name || 'Consulta'}`
                    }] : []),
                ]} />}
                <div className={styles.actions}><Button type="primary" size="large" icon={<ArrowRightOutlined />} loading={saving} onClick={submit}>Aperturar orden de atención</Button></div>
            </Card>
        </>}

        <Modal open={patientModalOpen} title={patient ? 'Revisar datos del paciente' : 'Registrar paciente'} footer={null} width={1000} onCancel={() => setPatientModalOpen(false)} destroyOnClose>
            <PatientForm
                editingPatient={patient as any}
                initialDni={dni}
                organizationId={organizationId}
                organizationName={organizationName}
                onClose={() => setPatientModalOpen(false)}
                onSaved={finishPatient}
                submitLabel="Continuar"
            />
        </Modal>
    </div>;
};

export default connect(null, {
    getPersonalRecordInformation,
    postMedicalRecordOrderAttention,
    getAppointmentsByOrganization,
    getOrganizationMedicById,
    getOrganizationMedics,
    getOrganizationManagementSpecialities,
})(DirectAttentionManagement);
